import { useRef } from 'react';
import { useRealtimeInvalidation } from '@/hooks/useRealtimeInvalidation';
import { phoneCallsService } from '../services/phoneCallsService';
import type { PhoneCallWithRelations } from '../types';

/** Hoe lang een screen-pop zichtbaar blijft (auto-dismiss). */
export const INCOMING_POP_DURATION_MS = 30_000;

/** Maximaal aantal gelijktijdige pops; oudste gesprek eerst. */
const MAX_ACTIVE_POPS = 2;

/**
 * Globale detectie van inkomende (rinkelende) oproepen voor de screen-pop.
 *
 * Reageert uitsluitend op realtime-ticks van `phone_calls` (geen polling):
 * bij elke tik worden de recente rinkelende inbound-gesprekken opgehaald via
 * `phoneCallsService.listRinging()`. Per `call_id` wordt maximaal één pop
 * getoond (Voys stuurt meerdere status-events per gesprek), met hooguit
 * {@link MAX_ACTIVE_POPS} pops tegelijk.
 *
 * @param showPop - Wordt aangeroepen voor elk nieuw rinkelend gesprek dat een
 *   pop verdient; de UI-kant (toast) leeft in `components/telephony`.
 */
export function useIncomingCallPop(showPop: (call: PhoneCallWithRelations) => void): void {
  const showPopRef = useRef(showPop);
  showPopRef.current = showPop;

  /** call_id's die al een pop kregen (of hun kans voorbij lieten gaan). */
  const seenRef = useRef<Set<string>>(new Set());
  /** Tijdstippen van recent getoonde pops, om gelijktijdige pops te begrenzen. */
  const shownAtRef = useRef<number[]>([]);
  /** Voorkomt parallelle fetches; een tik tijdens een fetch plant één re-run. */
  const inFlightRef = useRef(false);
  const rerunRef = useRef(false);

  const check = async () => {
    if (inFlightRef.current) {
      rerunRef.current = true;
      return;
    }
    inFlightRef.current = true;
    try {
      const ringing = await phoneCallsService.listRinging();
      const fresh = ringing.filter((call) => !seenRef.current.has(call.call_id));
      if (fresh.length > 0) {
        const now = Date.now();
        shownAtRef.current = shownAtRef.current.filter(
          (shownAt) => now - shownAt < INCOMING_POP_DURATION_MS,
        );
        const slots = Math.max(0, MAX_ACTIVE_POPS - shownAtRef.current.length);
        // Alles markeren als gezien: één pop per gesprek, ook als het door de
        // limiet buiten de boot valt (dan liever geen verlate pop).
        for (const call of fresh) seenRef.current.add(call.call_id);
        for (const call of fresh.slice(0, slots)) {
          shownAtRef.current.push(now);
          showPopRef.current(call);
        }
      }
    } catch (error) {
      // Stil falen: een gemiste pop mag de rest van de app niet storen.
      console.error('[useIncomingCallPop] Rinkelende gesprekken ophalen mislukt:', error);
    } finally {
      inFlightRef.current = false;
      if (rerunRef.current) {
        rerunRef.current = false;
        void check();
      }
    }
  };

  useRealtimeInvalidation('phone_calls', () => {
    void check();
  });
}
